import {
  Badge,
  Box,
  Button,
  Card,
  Stack,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import { InputSearchbox } from "components/Form/InputSearchbox";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import styled from "styled-components";
import {
  Pagination,
  PaginationDetail,
  PaginationFilter,
} from "components/Pagination";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import moment from "moment";
import {
  getAllRaffleCities,
  getAllRaffleOcrActivities,
  setModalGetReceiptFormOpen,
} from "redux/actions";
import FilterKit from "components/FilterKit";
import { IconPlusRounded } from "@aksara-ui/icons";
import { RaffleOcrGetManualForm } from "./components";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 30px;
  gap: 20px;
`;

const RaffleOcrActivityContent: React.FC = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 576 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const { isLoading } = useSelector((state: any) => state.globalReducer);
  const { raffleOcrActivities } = useSelector(
    (state: any) => state.raffleOcrActivityReducer
  );
  const { currentPage, totalPage, limitPage, lengthPage, filterCityPage } =
    useSelector((state: any) => state.raffleOcrActivityReducer);
  const { isModalGetReceiptFormOpen } = useSelector(
    (state: any) => state.receiptRaffleReducer
  );
  const { citiesFilter } = useSelector((state: any) => state.raffleReducer);

  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);

  const handleOpenFormModal = () => {
    dispatch(setModalGetReceiptFormOpen(true) as any);
  };

  const handleCloseFormModal = () => {
    dispatch(setModalGetReceiptFormOpen(false) as any);
  };

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
    dispatch(
      getAllRaffleOcrActivities(
        1,
        event.target.value,
        limitPage,
        filterCityPage
      ) as any
    );
  };

  const handleClearSearchChange = () => {
    setSearchValue("");
    dispatch(
      getAllRaffleOcrActivities(
        currentPage,
        "",
        limitPage,
        filterCityPage
      ) as any
    );
  };

  useEffect(() => {
    dispatch(getAllRaffleCities() as any);
    dispatch(getAllRaffleOcrActivities() as any);
  }, [dispatch]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <HeaderWrapper
        display={"flex"}
        flexDirection={"column"}
        style={{ gap: 20 }}
      >
        <Box display={"flex"}>
          <Text scale={[500, 600]} fontWeight={700}>
            Raffle OCR Activity
          </Text>
        </Box>

        <Box
          display={"flex"}
          flexDirection={isMobile ? "column" : "row"}
          justifyContent={"space-between"}
          style={{ gap: isMobile ? 10 : 20 }}
        >
          <Box
            display={"flex"}
            flexDirection={isTablet ? "column" : "row"}
            justifyContent={"space-between"}
            style={{ gap: 5 }}
          >
            <InputSearchbox
              autoComplete="off"
              name="search"
              placeholder="Search..."
              size={32}
              onChange={handleSearchChange}
              onClearButton={handleClearSearchChange}
            />

            <FilterKit
              orderByOptions={[
                {
                  key: citiesFilter,
                  label: "City",
                  options: citiesFilter.map((city: any) => {
                    return {
                      label: city.city,
                      value: city.id ?? "",
                    };
                  }),
                },
              ]}
              onChangeOrder={(e: any) => {
                dispatch(
                  getAllRaffleOcrActivities(
                    1,
                    "",
                    limitPage,
                    e[citiesFilter] ? e[citiesFilter].value : ""
                  ) as any
                );
              }}
            />
          </Box>

          <Stack
            display={"flex"}
            justifyContent={"right"}
            flexDirection={"row"}
            style={{ gap: isMobile ? 10 : 20, display: "none" }}
          >
            <Button
              variant="primary"
              borderRadius={10}
              style={{ fontWeight: 600 }}
              icon={IconPlusRounded}
              iconPosition={"left"}
              size={32}
              onClick={() => handleOpenFormModal()}
            >
              Push Manual OCR
            </Button>
          </Stack>
        </Box>
      </HeaderWrapper>

      <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
        <Table>
          <TableHead
            position={"sticky"}
            top={0}
            backgroundColor={theme.colors.grey02}
          >
            <TableHeadRow>
              <TableHeadCell>User</TableHeadCell>
              <TableHeadCell>Merchant Address</TableHeadCell>
              <TableHeadCell>Merchant Name</TableHeadCell>
              <TableHeadCell>Receipt Date</TableHeadCell>
              <TableHeadCell>Receipt Time</TableHeadCell>
              <TableHeadCell>Receipt Number</TableHeadCell>
              <TableHeadCell>Amount</TableHeadCell>
              <TableHeadCell>Total Raffle Earn</TableHeadCell>
              <TableHeadCell>Date/Time</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodyRowsSkeleton rowsNumber={10} columnsNumber={9} />
            ) : !raffleOcrActivities.length ? (
              <TableBodyRow>
                <TableBodyCell colSpan={9} textAlign={"center"}>
                  No data available in table
                </TableBodyCell>
              </TableBodyRow>
            ) : (
              raffleOcrActivities.map((ocr: any) => {
                return (
                  <TableBodyRow key={ocr.id}>
                    <TableBodyCell>
                      {ocr.user ? ocr.user.name : "-"}
                    </TableBodyCell>
                    <TableBodyCell>{ocr.merchant_address}</TableBodyCell>
                    <TableBodyCell>{ocr.merchant_name}</TableBodyCell>
                    <TableBodyCell>{ocr.receipt_date}</TableBodyCell>
                    <TableBodyCell>{ocr.receipt_time}</TableBodyCell>
                    <TableBodyCell>{ocr.receipt_number}</TableBodyCell>
                    <TableBodyCell>
                      <Badge
                        size="md"
                        variant="success"
                        style={{
                          paddingLeft: 20,
                          paddingRight: 20,
                          fontWeight: 600,
                        }}
                      >
                        {ocr.total_amount.toLocaleString()}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>
                      <Badge
                        size="md"
                        variant="default"
                        style={{
                          paddingLeft: 20,
                          paddingRight: 20,
                          fontWeight: 600,
                        }}
                      >
                        {ocr.total_raffle_earns}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>
                      {moment(ocr.created_at).format("DD MMMM YYYY, HH:mm:ss")}
                    </TableBodyCell>
                  </TableBodyRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingY={10}
      >
        {!isMobile ? (
          <PaginationDetail
            page={currentPage}
            limit={limitPage}
            length={lengthPage}
          />
        ) : (
          ""
        )}
        <Pagination
          current={currentPage}
          total={totalPage}
          onSelect={(select: any) =>
            dispatch(
              getAllRaffleOcrActivities(
                select,
                searchValue,
                limitPage,
                filterCityPage
              ) as any
            )
          }
        />
        <PaginationFilter
          selectedItem={limit}
          onChange={(selectedItem) => {
            if (selectedItem) {
              setLimit(selectedItem);
              dispatch(
                getAllRaffleOcrActivities(
                  1,
                  searchValue,
                  selectedItem,
                  filterCityPage
                ) as any
              );
            }
          }}
          items={[10, 20, 30, 40, 50]}
        />
      </Box>

      <RaffleOcrGetManualForm
        isOpen={isModalGetReceiptFormOpen}
        onClose={() => handleCloseFormModal()}
      />
    </Box>
  );
};

export default RaffleOcrActivityContent;
